import React, { useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import MyDiscoveries from "../Pages/MyDiscoveries";
import Login from "../Pages/Login";
import SignUp from "../Pages/SignUp";
import ConfirmationCode from "../Pages/ConfirmationCode";
import ResetPassword from "../Pages/ResetPassword";
import Discovery from "../Pages/Discovery";
import ValueMap from "../Pages/ValueMap";
import ErrorPage, { NotFound } from "../Pages/ErrorPage";
import Report from "../Pages/Report";
import InitializationError from "../Pages/InitializationError";
import PrintReport from "../Pages/PrintReport";
import Logout from "../Pages/Logout";
import Callback from "../Pages/Callback";
import {
  hasPermission,
  isAuthenticated,
  isInitialLoginComplete,
  selectInitializationError,
  selectRequestData,
  selectRequestState,
  selectUserId
} from "../store/reducers";
import { bindActionCreators } from "redux";
import { getMyUserInformation, logout } from "../store/actions/authentication";
import LoadingState from "../utils/loading-state";
import {
  getUserSettings,
  setUserSettings
} from "../store/actions/user-settings";
import Invitation from "../Pages/Invitation";
import ForgotPassword from "../Pages/ForgotPassword/ForgotPassword";
import CreateDiscovery from "../Pages/CreateDiscovery";
import LeadGeneration from "../Pages/LeadGeneration";
import actionTypes from "../store/actionTypes";
import Organization from "../Pages/Organization";
import useLoadingState from "../utils/use-loading-state";
import Permissions from "../utils/permissions";
import GrantDiscoveryAccess from "../Pages/GrantDiscoveryAccess";
import pusherService from "../services/pusher.service";
import ChannelNames from "../utils/channel-names";
import httpService from "../services/http.service";
import ProtectedRoute from "./ProtectedRoute";
import CuvamaRoute from "./CuvamaRoute";
import NotesPanel from "../components/Notes/NotesPanel";
import AutoConfiguration from "../Pages/AutoConfiguration";
import LoadingPage from "../Pages/AutoConfiguration/LoadingPage";

const Routes = ({
  userId,
  isInitialLoginComplete,
  isAuthenticated,
  userSettings,
  initializationError,
  userInfoLoadingState,
  canSeeMyDiscoveries,
  getMyUserInformation,
  getUserSettings,
  setUserSettings,
  canSeeOrgAdmin,
  logout
}) => {
  let location = useLocation();

  /*
    Resubscribe to the auth channel when the User navigates to a 
    new page in case "Pusher" was disconnected (due to a long running 
    browser session, for example)
   */

  useEffect(() => {
    if (userId) {
      const tenantId = httpService.getTenantId();
      const channelName = ChannelNames.AUTH({ userId, tenantId });
      if (!pusherService.isSubscribed(channelName)) {
        pusherService.subscribe({
          channelName,
          eventName: "logout",
          callback: () => {
            logout();
          }
        });
      }
    }
  }, [location, userId]);

  useEffect(() => {
    getMyUserInformation();
  }, [isAuthenticated]);

  useLoadingState(userInfoLoadingState, () => {
    if (isAuthenticated) {
      getUserSettings();
    }
  });

  useEffect(() => {
    if (isAuthenticated && userSettings && !userSettings.timeZone) {
      setUserSettings({
        ...userSettings,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
    }
  }, [userSettings]);

  if (initializationError) {
    return handleInitialization({ initializationError });
  }

  if (!isInitialLoginComplete) {
    return null;
  } else {
    switch (userInfoLoadingState) {
      case LoadingState.IN_PROGRESS:
      case LoadingState.UNINITIALIZED:
        return null;
      case LoadingState.FAILED:
        return <ErrorPage />;
      default:
        return (
          <Switch>
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
              hasPermission={canSeeMyDiscoveries}
              exact
              path="/MyDiscoveries"
              component={MyDiscoveries}
            />
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
              path="/discoveries/create"
              component={CreateDiscovery}
            />
            <CuvamaRoute
              requiresAuthentication={true}
              requiresPermission={Permissions.SEE_DISCOVERY_NOTES}
              path="/discoveries/:discoveryId/notes"
              component={NotesPanel}
            />
            <CuvamaRoute
              supportsIFrame
              path={"/discoveries/lead-generation"}
              component={LeadGeneration}
              exact
            />
            <CuvamaRoute
              supportsIFrame
              requiresAuthentication={true}
              path="/discoveries/:discoveryId"
              component={Discovery}
            />
            <CuvamaRoute
              requiresAuthentication={true}
              requiresPermission={Permissions.SEE_CANVAS}
              path="/valuemaps/:discoveryId"
              component={ValueMap}
            />
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
              hasPermission={canSeeOrgAdmin}
              path="/organizations/:path"
              component={Organization}
            />

            <ProtectedRoute
              isAuthenticated={isAuthenticated}
              path="/access/:accessId/grant"
              component={GrantDiscoveryAccess}
            />

            <CuvamaRoute
              exact
              path={"/auto-configuration"}
              component={AutoConfiguration}
              requiresAuthentication={true}
              requiresFeature={"autoConfiguration"}
            />

            <CuvamaRoute
              exact
              path={"/auto-configuration/:jobId"}
              component={LoadingPage}
              requiresAuthentication={true}
              requiresFeature={"autoConfiguration"}
            />

            <CuvamaRoute path="/login" component={Login} />
            <CuvamaRoute path="/sign-up" component={SignUp} />
            <CuvamaRoute
              path="/verification-code"
              component={ConfirmationCode}
            />
            <CuvamaRoute path="/forgot-password" component={ForgotPassword} />
            <CuvamaRoute path="/reset-password" component={ResetPassword} />
            <CuvamaRoute path="/logout" component={Logout} />
            <Route path={"/callback"} component={Callback} exact />

            <CuvamaRoute
              supportsIFrame
              path="/reports/:reportId"
              component={Report}
              exact
            />
            <CuvamaRoute
              supportsIFrame
              path="/reports/:reportId/:section"
              component={PrintReport}
            />

            <CuvamaRoute
              path="/invitations/:invitationId"
              component={Invitation}
            />

            <Redirect from="/" to="/MyDiscoveries" exact />
            <Route component={NotFound} />
          </Switch>
        );
    }
  }
};

const handleInitialization = ({ initializationError }) => {
  return initializationError ? (
    <Switch>
      <Route path="/initialization-error" component={InitializationError} />
      <Redirect to="/initialization-error" />
    </Switch>
  ) : (
    ""
  );
};

const mapStateToProps = (state) => ({
  userId: selectUserId(state),
  isAuthenticated: isAuthenticated(state),
  isInitialLoginComplete: isInitialLoginComplete(state),
  initializationError: selectInitializationError(state),
  userInfoLoadingState: selectRequestState(
    state,
    actionTypes.USER_INFO_REQUEST
  ),
  userSettings: selectRequestData(state, actionTypes.GET_USER_SETTINGS_REQUEST),
  canSeeMyDiscoveries: hasPermission(state, Permissions.SEE_MY_DISCOVERIES),
  canSeeOrgAdmin: hasPermission(state, Permissions.SEE_ORG_ADMIN)
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getMyUserInformation,
      getUserSettings,
      setUserSettings,
      logout
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(Routes);
